import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import {
  Clear as ClearIcon,
  FileDownloadOutlined as ExportIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { toast } from "react-hot-toast";
import { PaginatedTable } from "../../components";
import { baseAxios } from "../../utils/config";
import { colors } from "../../theme/colors";

const API = "gateway/m4vjjp9nh5";
const CONTROL_RADIUS = "6px";

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0.01) return "$0.00";
  return `$${n.toFixed(2)}`;
}

function textCell(value) {
  return (
    <Typography
      component="span"
      fontSize={12}
      sx={{ overflowWrap: "break-word", lineHeight: 1.3 }}
    >
      {value || "—"}
    </Typography>
  );
}

function isLeafProduct(row) {
  const id = row?.product_id;
  if (id == null) return false;
  return /^\d+$/.test(String(id));
}

function compareRows(a, b, field) {
  const va = a[field];
  const vb = b[field];
  if (
    field === "product_cost" ||
    field === "product_container_size" ||
    field === "product_waste_ratio"
  ) {
    return (Number(va) || 0) - (Number(vb) || 0);
  }
  return String(va ?? "").localeCompare(String(vb ?? ""), undefined, {
    sensitivity: "base",
  });
}

function FieldFilter({
  value,
  onChange,
  anchorEl,
  setAnchorEl,
  options,
  ariaLabel,
  allLabel,
}) {
  const active = value.length > 0;
  const [filterSearch, setFilterSearch] = useState("");
  const filteredOptions = useMemo(() => {
    const query = filterSearch.trim().toLowerCase();
    if (!query) return options;
    return options.filter((option) =>
      String(option).toLowerCase().includes(query)
    );
  }, [filterSearch, options]);

  useEffect(() => {
    if (!anchorEl) setFilterSearch("");
  }, [anchorEl]);

  const toggleOption = (option) => {
    onChange(
      value.includes(option)
        ? value.filter((item) => item !== option)
        : [...value, option]
    );
  };

  return (
    <Box
      component="span"
      onMouseDown={(event) => event.stopPropagation()}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        flexShrink: 0,
        ml: 0.25,
      }}
    >
      <IconButton
        size="small"
        aria-label={ariaLabel}
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl)}
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl((prev) =>
            prev === event.currentTarget ? null : event.currentTarget
          );
        }}
        sx={{
          p: 0.35,
          color: active ? colors.gold : "rgba(255,255,255,0.75)",
          "&:hover": { color: active ? colors.gold : "common.white" },
        }}
      >
        <FilterListIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            width: 260,
            maxHeight: 360,
            py: 0.5,
            borderRadius: CONTROL_RADIUS,
          },
        }}
        MenuListProps={{ dense: true }}
      >
        <Box
          sx={{
            px: 1,
            py: 0.75,
            position: "sticky",
            top: 0,
            bgcolor: "background.paper",
            zIndex: 1,
          }}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search options..."
            value={filterSearch}
            onChange={(event) => setFilterSearch(event.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: CONTROL_RADIUS,
                fontSize: "0.82rem",
                height: 34,
              },
            }}
          />
        </Box>
        <MenuItem dense sx={{ minHeight: 32, py: 0.25 }} onClick={() => onChange([])}>
          <ListItemText
            primary={allLabel}
            primaryTypographyProps={{ sx: { fontSize: "0.82rem" } }}
          />
        </MenuItem>
        {filteredOptions.length === 0 ? (
          <MenuItem disabled dense sx={{ minHeight: 32, py: 0.25 }}>
            <ListItemText
              primary="No matching options"
              primaryTypographyProps={{ sx: { fontSize: "0.82rem" } }}
            />
          </MenuItem>
        ) : null}
        {filteredOptions.map((option) => (
          <MenuItem
            key={option}
            dense
            selected={value.includes(option)}
            onClick={() => toggleOption(option)}
            sx={{ minHeight: 32, py: 0.25 }}
          >
            <Checkbox
              size="small"
              disableRipple
              checked={value.includes(option)}
              sx={{
                p: 0.25,
                mr: 0.75,
                color: colors.gold,
                "&.Mui-checked": { color: colors.gold },
                "& .MuiSvgIcon-root": { fontSize: 18 },
              }}
            />
            <ListItemText
              primary={option}
              primaryTypographyProps={{ sx: { fontSize: "0.82rem" } }}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

/**
 * Product Catalogue — recognized products with cost / size details
 */
export default function ManufacturersSQL() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("product_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [manufacturerFilter, setManufacturerFilter] = useState([]);
  const [manufacturerFilterMenuAnchor, setManufacturerFilterMenuAnchor] =
    useState(null);
  const [brandFilter, setBrandFilter] = useState([]);
  const [brandFilterMenuAnchor, setBrandFilterMenuAnchor] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [categoryFilterMenuAnchor, setCategoryFilterMenuAnchor] =
    useState(null);
  const [typeFilter, setTypeFilter] = useState([]);
  const [typeFilterMenuAnchor, setTypeFilterMenuAnchor] = useState(null);

  const loadCatalogue = useCallback(async () => {
    setLoading(true);
    try {
      const result = await baseAxios.post(API);
      const items = result?.data?.Items || result?.data || [];
      const products = (Array.isArray(items) ? items : [])
        .filter(isLeafProduct)
        .map((item) => ({
          ...item,
          product_cost: Number(item.product_cost) || 0,
          product_container_size: Number(item.product_container_size) || 0,
          product_waste_ratio: Number(item.product_waste_ratio) || 0,
        }));
      setRows(products);
    } catch (error) {
      setRows([]);
      if (error.response?.status === 403) {
        toast.error("Not allowed to see product catalogue", { duration: 3000 });
      } else {
        toast.error("Error loading product catalogue", { duration: 3000 });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCatalogue();
  }, [loadCatalogue]);

  const filterOptions = useMemo(() => {
    const manufacturers = new Set();
    const brands = new Set();
    const categories = new Set();
    const types = new Set();
    rows.forEach((row) => {
      if (row.manufacturer_name) manufacturers.add(row.manufacturer_name);
      if (row.brand_name) brands.add(row.brand_name);
      if (row.category_name) categories.add(row.category_name);
      if (row.product_type_name) types.add(row.product_type_name);
    });
    const sortOptions = (values) =>
      [...values].sort((a, b) =>
        String(a).localeCompare(String(b), undefined, { sensitivity: "base" })
      );
    return {
      manufacturers: sortOptions(manufacturers),
      brands: sortOptions(brands),
      categories: sortOptions(categories),
      types: sortOptions(types),
    };
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (
        manufacturerFilter.length > 0 &&
        !manufacturerFilter.includes(row.manufacturer_name)
      ) {
        return false;
      }
      if (brandFilter.length > 0 && !brandFilter.includes(row.brand_name)) {
        return false;
      }
      if (
        categoryFilter.length > 0 &&
        !categoryFilter.includes(row.category_name)
      ) {
        return false;
      }
      if (
        typeFilter.length > 0 &&
        !typeFilter.includes(row.product_type_name)
      ) {
        return false;
      }
      const q = search.trim().toLowerCase();
      if (!q) return true;
      const haystack = [
        row.manufacturer_name,
        row.brand_name,
        row.category_name,
        row.product_type_name,
        row.product_name,
        row.process_name,
        row.product_container_description,
        row.product_upc_code,
        formatMoney(row.product_cost),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [
    brandFilter,
    categoryFilter,
    manufacturerFilter,
    rows,
    search,
    typeFilter,
  ]);

  const sortedRows = useMemo(() => {
    const copy = [...filteredRows];
    copy.sort((a, b) => {
      const c = compareRows(a, b, sortBy);
      return sortOrder === "asc" ? c : -c;
    });
    return copy;
  }, [filteredRows, sortBy, sortOrder]);

  const clearAllFilters = useCallback(() => {
    setManufacturerFilter([]);
    setBrandFilter([]);
    setCategoryFilter([]);
    setTypeFilter([]);
    setSearch("");
  }, []);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    manufacturerFilter.forEach((v) =>
      chips.push({
        key: `mfg-${v}`,
        label: `Manufacturer: ${v}`,
        onDelete: () =>
          setManufacturerFilter((prev) => prev.filter((x) => x !== v)),
      })
    );
    brandFilter.forEach((v) =>
      chips.push({
        key: `brand-${v}`,
        label: `Brand: ${v}`,
        onDelete: () => setBrandFilter((prev) => prev.filter((x) => x !== v)),
      })
    );
    categoryFilter.forEach((v) =>
      chips.push({
        key: `cat-${v}`,
        label: `Category: ${v}`,
        onDelete: () =>
          setCategoryFilter((prev) => prev.filter((x) => x !== v)),
      })
    );
    typeFilter.forEach((v) =>
      chips.push({
        key: `type-${v}`,
        label: `Type: ${v}`,
        onDelete: () => setTypeFilter((prev) => prev.filter((x) => x !== v)),
      })
    );
    return chips;
  }, [brandFilter, categoryFilter, manufacturerFilter, typeFilter]);

  const handleSortChange = useCallback((field, order) => {
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const noopPagination = useCallback(() => {}, []);

  const handleExport = useCallback(async () => {
    if (!sortedRows.length) {
      toast.error("No data to export", { duration: 2500 });
      return;
    }
    try {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Product Catalogue");
      worksheet.columns = [
        { header: "Manufacturer", key: "manufacturer_name", width: 18 },
        { header: "Brand", key: "brand_name", width: 16 },
        { header: "Category", key: "category_name", width: 16 },
        { header: "Type", key: "product_type_name", width: 14 },
        { header: "Product Name", key: "product_name", width: 28 },
        { header: "Process", key: "process_name", width: 14 },
        {
          header: "Container Description",
          key: "product_container_description",
          width: 22,
        },
        { header: "Container Size", key: "product_container_size", width: 14 },
        { header: "Product Cost", key: "product_cost", width: 12 },
        { header: "Waste Ratio", key: "product_waste_ratio", width: 12 },
        { header: "UPC", key: "product_upc_code", width: 16 },
        { header: "Unit Sale Only", key: "product_unit_sale_only", width: 14 },
      ];
      worksheet.getRow(1).font = { bold: true };
      sortedRows.forEach((row) => {
        worksheet.addRow({
          manufacturer_name: row.manufacturer_name || "",
          brand_name: row.brand_name || "",
          category_name: row.category_name || "",
          product_type_name: row.product_type_name || "",
          product_name: row.product_name || "",
          process_name: row.process_name || "",
          product_container_description:
            row.product_container_description || "",
          product_container_size: row.product_container_size || "",
          product_cost: row.product_cost || 0,
          product_waste_ratio: row.product_waste_ratio || "",
          product_upc_code: row.product_upc_code || "",
          product_unit_sale_only: row.product_unit_sale_only || "",
        });
      });
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `ProductCatalogue_${dayjs().format("YYYY-MM-DD")}.xlsx`
      );
    } catch {
      toast.error("Export failed", { duration: 3000 });
    }
  }, [sortedRows]);

  const columns = useMemo(
    () => [
      {
        id: "manufacturer_name",
        label: "Manufacturer",
        field: "manufacturer_name",
        width: "10%",
        filtered: manufacturerFilter.length > 0,
        headerExtras: (
          <FieldFilter
            value={manufacturerFilter}
            onChange={setManufacturerFilter}
            anchorEl={manufacturerFilterMenuAnchor}
            setAnchorEl={setManufacturerFilterMenuAnchor}
            options={filterOptions.manufacturers}
            ariaLabel="Filter by manufacturer"
            allLabel="All manufacturers"
          />
        ),
        render: (v) => textCell(v),
      },
      {
        id: "brand_name",
        label: "Brand",
        field: "brand_name",
        width: "8%",
        filtered: brandFilter.length > 0,
        headerExtras: (
          <FieldFilter
            value={brandFilter}
            onChange={setBrandFilter}
            anchorEl={brandFilterMenuAnchor}
            setAnchorEl={setBrandFilterMenuAnchor}
            options={filterOptions.brands}
            ariaLabel="Filter by brand"
            allLabel="All brands"
          />
        ),
        render: (v) => textCell(v),
      },
      {
        id: "category_name",
        label: "Category",
        field: "category_name",
        width: "8%",
        filtered: categoryFilter.length > 0,
        headerExtras: (
          <FieldFilter
            value={categoryFilter}
            onChange={setCategoryFilter}
            anchorEl={categoryFilterMenuAnchor}
            setAnchorEl={setCategoryFilterMenuAnchor}
            options={filterOptions.categories}
            ariaLabel="Filter by category"
            allLabel="All categories"
          />
        ),
        render: (v) => textCell(v),
      },
      {
        id: "product_type_name",
        label: "Type",
        field: "product_type_name",
        width: "8%",
        filtered: typeFilter.length > 0,
        headerExtras: (
          <FieldFilter
            value={typeFilter}
            onChange={setTypeFilter}
            anchorEl={typeFilterMenuAnchor}
            setAnchorEl={setTypeFilterMenuAnchor}
            options={filterOptions.types}
            ariaLabel="Filter by type"
            allLabel="All types"
          />
        ),
        render: (v) =>
          v ? (
            <Chip
              size="small"
              label={v}
              variant="outlined"
              sx={{
                fontWeight: 600,
                borderRadius: CONTROL_RADIUS,
                borderColor: colors.divider,
              }}
            />
          ) : (
            "—"
          ),
      },
      {
        id: "product_name",
        label: "Product Name",
        field: "product_name",
        width: "14%",
        render: (v) => textCell(v),
      },
      {
        id: "process_name",
        label: "Process",
        field: "process_name",
        width: "8%",
        render: (v) => textCell(v),
      },
      {
        id: "product_container_description",
        label: "Container",
        field: "product_container_description",
        width: "10%",
        render: (v) => textCell(v),
      },
      {
        id: "product_container_size",
        label: "Size",
        field: "product_container_size",
        width: "6%",
        render: (v) => (v ? String(v) : "—"),
      },
      {
        id: "product_cost",
        label: "Cost",
        field: "product_cost",
        width: "7%",
        render: (v) => (
          <Typography component="span" fontWeight={600} fontSize={14}>
            {formatMoney(v)}
          </Typography>
        ),
      },
      {
        id: "product_waste_ratio",
        label: "Waste Ratio",
        field: "product_waste_ratio",
        width: "6%",
        render: (v) => (v == null || v === "" ? "—" : String(v)),
      },
      {
        id: "product_upc_code",
        label: "UPC",
        field: "product_upc_code",
        width: "8%",
        render: (v) => textCell(v),
      },
      {
        id: "product_unit_sale_only",
        label: "Unit Sale Only",
        field: "product_unit_sale_only",
        width: "7%",
        render: (v) => {
          const yes = String(v) === "1" || v === true || v === "true";
          return (
            <Chip
              size="small"
              label={yes ? "Yes" : "No"}
              variant={yes ? "filled" : "outlined"}
              sx={{
                fontWeight: 600,
                borderRadius: CONTROL_RADIUS,
                bgcolor: yes ? colors.gold : "transparent",
                color: yes ? colors.black : "text.secondary",
                borderColor: yes ? colors.gold : "divider",
              }}
            />
          );
        },
      },
    ],
    [
      brandFilter,
      brandFilterMenuAnchor,
      categoryFilter,
      categoryFilterMenuAnchor,
      filterOptions,
      manufacturerFilter,
      manufacturerFilterMenuAnchor,
      typeFilter,
      typeFilterMenuAnchor,
    ]
  );

  return (
    <Box sx={{ px: 8, py: 3, width: "100%", minWidth: 0, overflowX: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mt: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
            Product Catalogue
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Browse recognized products with cost, size, and classification
            details.
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Search product, brand, UPC…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              minWidth: 260,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "background.paper",
                borderRadius: CONTROL_RADIUS,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => setSearch("")}
                  >
                    <ClearIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <IconButton
            aria-label="Refresh catalogue"
            onClick={loadCatalogue}
            disabled={loading}
            size="medium"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              borderRadius: CONTROL_RADIUS,
              "&:hover": { bgcolor: "grey.100" },
            }}
          >
            <RefreshIcon />
          </IconButton>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ExportIcon />}
            onClick={handleExport}
            disabled={loading || !sortedRows.length}
            sx={{
              minHeight: 40,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: CONTROL_RADIUS,
            }}
          >
            Export
          </Button>
        </Box>
      </Box>

      {activeFilterChips.length > 0 ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            mb: 1.5,
          }}
        >
          {activeFilterChips.map((chip) => (
            <Chip
              key={chip.key}
              size="small"
              label={chip.label}
              onDelete={chip.onDelete}
              sx={{
                borderRadius: CONTROL_RADIUS,
                bgcolor: colors.black,
                color: colors.gold,
                fontWeight: 600,
                "& .MuiChip-deleteIcon": {
                  color: colors.gold,
                  "&:hover": { color: colors.white },
                },
              }}
            />
          ))}
          <Button
            size="small"
            onClick={clearAllFilters}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "text.secondary",
              minHeight: 28,
            }}
          >
            Clear filters
          </Button>
        </Box>
      ) : null}

      <PaginatedTable
        columns={columns}
        rows={sortedRows}
        totalRowCount={sortedRows.length}
        loading={loading}
        onPaginationChange={noopPagination}
        onSortChange={handleSortChange}
        initialOrderBy="product_name"
        initialOrder="asc"
        searchQuery={`${search}|${manufacturerFilter.join(",")}|${brandFilter.join(",")}|${categoryFilter.join(",")}|${typeFilter.join(",")}|${rows.length}`}
        clientSidePagination
        skeletonHeight="28px"
        getRowKey={(row) => row.product_id}
        fitWidth
        emptyMessage="No products found in catalogue"
      />
    </Box>
  );
}
